import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Messages } from "primereact/messages";
import React, { useRef, useState } from "react";
import { useUpdateDeviceLocation } from "hooks/useDevices";
import GoogleMap from "components/GoogleMapBox";
import { PlacesSearchBox } from "./PlacesSearchBox";
import { notify } from "utils/notify";
import { useQueryClient } from "@tanstack/react-query";

interface LatLng {
  lat: number;
  lng: number;
}

interface DeviceAddressMapProps {
  serialNumber: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  refetchQuery: () => void;
  zoom?: number;
  hideButton?: boolean;
  visible?: boolean;
  onClose: () => void;
}

const DeviceAddressMap: React.FC<DeviceAddressMapProps> = ({
  serialNumber,
  address,
  latitude,
  longitude,
  zoom = 15,
  refetchQuery,
  visible = false,
  onClose,
  hideButton = false,
}) => {
  const [deviceAddress, setDeviceAddress] = useState(address || "");
  const [markerPosition, setMarkerPosition] = useState<LatLng | null>(
    latitude && longitude ? { lat: latitude, lng: longitude } : null
  );
  const [buttonLoading, setButtonLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState<LatLng>(
    latitude && longitude
      ? { lat: latitude, lng: longitude }
      : { lat: -37.8136, lng: 144.9631 } // Default to Melbourne
  );
  const msg = useRef<Messages>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const queryClient = useQueryClient();
  const updateDeviceLocation = useUpdateDeviceLocation();

  // Function to save device address
  const saveDeviceAddress = async () => {
    if (!deviceAddress || !markerPosition) {
      msg.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select an address first",
        life: 3000,
      });
      return;
    }

    setButtonLoading(true);

    try {
      // Save device address using React Query mutation
      await updateDeviceLocation.mutateAsync({
        serialNumber,
        address: deviceAddress,
        coordinates: markerPosition,
      });

      notify.success("Device address updated successfully", {
        position: "top-right",
        autoClose: 3000,
      });

      // Refetch query to update the UI with new data
      refetchQuery();

      // Also invalidate any relevant queries that might be affected by this update
      queryClient.invalidateQueries({ queryKey: ["getDevice", serialNumber] });

      setButtonLoading(false);
      onClose();
    } catch (error) {
      console.error("Error saving device address:", error);
      msg.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update device address",
        life: 3000,
      });
      setButtonLoading(false);
    }
  };

  const renderMap = () => {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <PlacesSearchBox
          onPlaceSelect={(place) => {
            if (place && place.latitude && place.longitude) {
              // Update address in input
              const formattedAddress =
                place.formattedAddress || place.description;
              setDeviceAddress(formattedAddress);

              // Update marker position
              const newPosition = {
                lat: place.latitude,
                lng: place.longitude,
              };

              // Update both the marker and map center
              setMarkerPosition(newPosition);
              setMapCenter(newPosition);

              // Move the map to the new position
              if (mapRef.current) {
                mapRef.current.panTo(newPosition);
                mapRef.current.setZoom(15);
              }
            }
          }}
        />
        <div style={{ margin: "0.5rem 0" }}>
          <span>
            <strong>Current address: </strong>
            {deviceAddress || "No address set"}
          </span>
        </div>

        <div
          style={{
            flexGrow: 1,
            position: "relative",
            minHeight: "400px",
            marginBottom: "10px",
          }}
        >
          <GoogleMap
            address={deviceAddress}
            latitude={mapCenter.lat}
            longitude={mapCenter.lng}
            zoom={zoom}
            markers={
              markerPosition
                ? [
                    {
                      lat: markerPosition.lat,
                      lng: markerPosition.lng,
                      label: serialNumber,
                      onClick: () => {},
                    },
                  ]
                : []
            }
            setMapRef={(map) => {
              mapRef.current = map;
            }}
          />
        </div>

        <Messages ref={msg} />
      </div>
    );
  };

  const footerContent = (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: "10px",
        padding: "10px 0",
      }}
    >
      <Button
        type="button"
        label="Cancel"
        icon="pi pi-times"
        onClick={onClose}
        outlined
      />
      <Button
        type="button"
        label="Save"
        icon="pi pi-check"
        onClick={saveDeviceAddress}
        loading={buttonLoading}
        severity="secondary"
        autoFocus
      />
    </div>
  );

  return (
    <Dialog
      header="Configure Device Address"
      visible={visible}
      style={{ width: "90%", maxWidth: "1000px", maxHeight: "90vh" }}
      contentStyle={{
        height: "calc(80vh - 120px)",
        padding: "20px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
      onHide={onClose}
      modal
      footer={footerContent}
      dismissableMask
      closeOnEscape
    >
      {renderMap()}
    </Dialog>
  );
};

export default DeviceAddressMap;
